import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GiLotusFlower } from 'react-icons/gi';
import { FaPlus, FaEdit, FaTrash, FaSignOutAlt, FaRupeeSign } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Loader from '../components/Loader';

const API = process.env.REACT_APP_API_URL;

const emptyForm = { name: '', description: '', price: '', category: 'Dhoop Batti', inStock: true };

const AdminDashboard = () => {
  const { token, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAdmin) navigate('/admin');
    else fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/products`);
      setProducts(res.data);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const openAdd = () => {
    setEditProduct(null);
    setForm(emptyForm);
    setImage(null); setPreview(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setForm({ name: p.name, description: p.description, price: p.price, category: p.category, inStock: p.inStock });
    setPreview(p.image); setImage(null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);
  try {
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    if (image) data.append('image', image);
    
    const currentToken = localStorage.getItem('adminToken');
    console.log('Sending token:', currentToken);

    const res = await axios({
      method: editProduct ? 'put' : 'post',
      url: editProduct
        ? `${process.env.REACT_APP_API_URL}/api/products/${editProduct._id}`
        : `${process.env.REACT_APP_API_URL}/api/products`,
      data,
      headers: {
        'Authorization': `Bearer ${currentToken}`,
      },
    });
    
    console.log('Success:', res.data);
    toast.success(editProduct ? 'Product updated! 🙏' : 'Product added! 🙏');
    setShowForm(false);
    fetchProducts();
  } catch (err) {
    console.log('Error:', err.response?.data || err.message);
    toast.error('Something went wrong: ' + (err.response?.data?.message || err.message));
  }
  setSubmitting(false);
};

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/api/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Product deleted');
      fetchProducts();
    } catch { toast.error('Delete failed'); }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const inputStyle = {
    width: '100%', padding: '0.8rem 1rem',
    background: '#fffdf5', border: '1px solid #e8c96d',
    borderRadius: '8px', fontFamily: 'Crimson Text',
    fontSize: '1rem', color: '#2C1810',
    outline: 'none', marginBottom: '1rem'
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FDF6E3', paddingTop: '0' }}>

      {/* Dashboard Header */}
      <div style={{
        background: 'linear-gradient(135deg, #2C1810, #4a2010)',
        padding: '1.5rem 2rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <GiLotusFlower size={32} color="#D4AF37" />
          <div>
            <h1 style={{ fontFamily: 'Cinzel', color: '#F5D76E', fontSize: '1.2rem', letterSpacing: '3px' }}>
              ADMIN DASHBOARD
            </h1>
            <p style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.75rem' }}>
              {products.length} products listed
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={openAdd}
            style={{
              background: 'linear-gradient(135deg, #D4AF37, #FF6B00)',
              color: '#fff', border: 'none', padding: '0.7rem 1.5rem',
              borderRadius: '50px', fontFamily: 'Cinzel', fontSize: '0.8rem',
              letterSpacing: '2px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}>
            <FaPlus /> ADD PRODUCT
          </motion.button>
          <motion.button whileHover={{ scale: 1.05 }} onClick={handleLogout}
            style={{
              background: 'transparent', border: '1px solid #8B6914',
              color: '#8B6914', padding: '0.7rem 1.2rem',
              borderRadius: '50px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
              fontFamily: 'Cinzel', fontSize: '0.8rem'
            }}>
            <FaSignOutAlt /> LOGOUT
          </motion.button>
        </div>
      </div>

      {/* Product Grid */}
      <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
        {loading ? <Loader /> : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 0' }}>
            <GiLotusFlower size={50} color="#D4AF37" />
            <h3 style={{ fontFamily: 'Cinzel', color: '#8B6914', marginTop: '1rem' }}>No products yet</h3>
            <p style={{ color: '#c9a96e', fontFamily: 'Crimson Text' }}>Click "ADD PRODUCT" to get started</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {products.map((p, i) => (
              <motion.div key={p._id}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                style={{
                  background: 'linear-gradient(145deg, #fffdf5, #fdf0d0)',
                  border: '1px solid #e8c96d', borderRadius: '16px', overflow: 'hidden'
                }}>
                <img src={p.image} alt={p.name}
                  style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                <div style={{ padding: '1.2rem' }}>
                  <h3 style={{ fontFamily: 'Cinzel', color: '#2C1810', fontSize: '0.95rem', marginBottom: '0.4rem' }}>
                    {p.name}
                  </h3>
                  <p style={{ color: '#6b4c1e', fontFamily: 'Crimson Text', fontSize: '0.9rem',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '0.8rem' }}>
                    {p.description}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FaRupeeSign color="#D4AF37" size={12} />
                      <span style={{ fontFamily: 'Cinzel', color: '#D4AF37', fontWeight: '600' }}>{p.price}</span>
                    </div>
                    <span style={{
                      background: p.inStock ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                      color: p.inStock ? '#15803d' : '#dc2626',
                      padding: '0.2rem 0.7rem', borderRadius: '50px',
                      fontSize: '0.75rem', fontFamily: 'Inter'
                    }}>
                      {p.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.8rem' }}>
                    <motion.button whileHover={{ scale: 1.05 }} onClick={() => openEdit(p)}
                      style={{
                        flex: 1, padding: '0.6rem', background: 'transparent',
                        border: '1px solid #D4AF37', color: '#D4AF37',
                        borderRadius: '8px', cursor: 'pointer',
                        fontFamily: 'Cinzel', fontSize: '0.75rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}>
                      <FaEdit /> EDIT
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.05 }} onClick={() => handleDelete(p._id)}
                      style={{
                        flex: 1, padding: '0.6rem',
                        background: 'rgba(239,68,68,0.1)',
                        border: '1px solid rgba(239,68,68,0.3)', color: '#dc2626',
                        borderRadius: '8px', cursor: 'pointer',
                        fontFamily: 'Cinzel', fontSize: '0.75rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}>
                      <FaTrash /> DELETE
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2000, padding: '1rem'
            }}
            onClick={e => e.target === e.currentTarget && setShowForm(false)}>

            <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
              style={{
                background: '#FDF6E3', borderRadius: '20px',
                padding: '2rem', width: '100%', maxWidth: '500px',
                maxHeight: '90vh', overflowY: 'auto',
                border: '1px solid #e8c96d'
              }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontFamily: 'Cinzel', color: '#2C1810', fontSize: '1.1rem', letterSpacing: '3px' }}>
                  {editProduct ? 'EDIT PRODUCT' : 'ADD PRODUCT'}
                </h2>
                <button onClick={() => setShowForm(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#8B6914' }}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <label style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.75rem', letterSpacing: '1px' }}>PRODUCT NAME *</label>
                <input style={inputStyle} placeholder="e.g. Chandan Dhoop Batti"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />

                <label style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.75rem', letterSpacing: '1px' }}>DESCRIPTION *</label>
                <textarea style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                  placeholder="Describe the product, its fragrance, benefits..."
                  value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />

                <label style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.75rem', letterSpacing: '1px' }}>PRICE (₹) *</label>
                <input style={inputStyle} type="number" placeholder="e.g. 149"
                  value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required />

                <label style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.75rem', letterSpacing: '1px' }}>CATEGORY</label>
                <select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option>Dhoop Batti</option>
                  <option>Dhoop Cone</option>
                  <option>Sambrani</option>
                  <option>Agarbatti</option>
                  <option>Special Collection</option>
                </select>

                <label style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.75rem', letterSpacing: '1px' }}>AVAILABILITY</label>
                <select style={inputStyle} value={form.inStock}
                  onChange={e => setForm({ ...form, inStock: e.target.value === 'true' })}>
                  <option value="true">In Stock</option>
                  <option value="false">Out of Stock</option>
                </select>

                <label style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.75rem', letterSpacing: '1px' }}>
                  PRODUCT IMAGE {!editProduct && '*'}
                </label>
                <input type="file" accept="image/*" onChange={handleImageChange}
                  style={{ ...inputStyle, padding: '0.6rem' }}
                  required={!editProduct} />

                {preview && (
                  <img src={preview} alt="preview"
                    style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1rem', border: '1px solid #e8c96d' }} />
                )}

                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  type="submit" disabled={submitting}
                  style={{
                    width: '100%', padding: '1rem',
                    background: 'linear-gradient(135deg, #D4AF37, #FF6B00)',
                    color: '#fff', border: 'none', borderRadius: '10px',
                    fontFamily: 'Cinzel', fontSize: '0.9rem', letterSpacing: '2px',
                    cursor: 'pointer', boxShadow: '0 8px 25px rgba(212,175,55,0.3)'
                  }}>
                  {submitting ? 'Saving...' : editProduct ? 'UPDATE PRODUCT' : 'ADD PRODUCT'}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;